import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { aiChatService } from '../services/aiChatService';
import DeploymentOptions from '../components/DeploymentOptions';

export function PlayFormatter() {
  const [contentInput, setContentInput] = useState('');
  const [format, setFormat] = useState('standard');
  const [style, setStyle] = useState('modern');
  const [includeNotes, setIncludeNotes] = useState(true);
  const [formattedContent, setFormattedContent] = useState('');
  const [isFormatting, setIsFormatting] = useState(false);

  const formatOptions = [
    { value: 'standard', label: 'Standard Play Format' },
    { value: 'musical', label: 'Musical Format' },
    { value: 'screenplay', label: 'Screenplay Format' },
    { value: 'radio', label: 'Radio Play Format' }
  ];

  const styleOptions = [
    { value: 'modern', label: 'Modern Style' },
    { value: 'classic', label: 'Classic Style' },
    { value: 'minimal', label: 'Minimal Style' },
    { value: 'detailed', label: 'Detailed Style' }
  ];

  const quickStartGuides = [
    {
      title: "Text Preparation",
      description: "Prepare your text by separating dialogue, actions, and scene descriptions with line breaks.",
      keywords: ["Dialogue", "Actions", "Scenes"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "Format Selection",
      description: "Choose the appropriate format for your play type and adjust styling preferences.",
      keywords: ["Format", "Style", "Layout"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
        </svg>
      )
    },
    {
      title: "Review & Export",
      description: "Review the formatted output and export your properly formatted play.",
      keywords: ["Review", "Export", "Final"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setContentInput(transcribedText);
  };

  const formatContent = async () => {
    if (!contentInput.trim()) {
      toast.error('Please provide text to format');
      return;
    }

    setIsFormatting(true);
    try {
      const prompt = `Format the following text into a ${format} play format using ${style} style. 
${includeNotes ? 'Include stage directions and technical notes.' : 'Exclude stage directions and technical notes.'}

Text to format:
${contentInput}

Please maintain the original dialogue and scene descriptions, only reformatting them according to standard play formatting conventions. Do not add or modify any content unless it's necessary for proper formatting (e.g., adding character names before dialogue if clearly implied).

Format rules to apply:
1. Character names in CAPS before their dialogue
2. Stage directions in (parentheses) or [brackets]
3. Scene headings in ALL CAPS
4. Proper indentation and spacing
5. Clear separation between dialogue and stage directions
6. Consistent formatting throughout`;

      const response = await aiChatService.generateResponse(prompt, [], 'playFormatter');
      
      if (response.error) {
        throw new Error(response.error);
      } else {
        const formattedText = response.content || response.message;
        if (formattedText) {
          setFormattedContent(formattedText);
          toast.success('Text formatted successfully!');
        } else {
          throw new Error('No formatted content received');
        }
      }
    } catch (error) {
      console.error('Error formatting content:', error);
      toast.error(error.message || 'Failed to format text. Please try again.');
    } finally {
      setIsFormatting(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  const downloadAsFile = () => {
    const element = document.createElement('a');
    const file = new Blob([formattedContent], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = 'formatted_play.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('File downloaded successfully!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-indigo-900 via-indigo-800 to-indigo-900 shadow-2xl overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-indigo-400/20 to-transparent"></div>
        </div>

        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-indigo-800/50 border border-indigo-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></div>
                <span className="text-sm text-indigo-100">Automatic Formatting</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Play Formatter
              </h1>
              <p className="text-lg text-indigo-200 max-w-xl">
                Transform your raw text into properly formatted play scripts
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          <div className="space-y-8">
            {/* Input Section */}
            <div className="space-y-6">
              {/* Voice Recorder */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Record Audio
                </label>
                <VoiceRecorder 
                  onTranscriptionComplete={handleTranscriptionComplete}
                  placeholder="Click to start recording your play..."
                />
              </div>

              {/* Text Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unformatted Text
                </label>
                <textarea
                  value={contentInput}
                  onChange={(e) => setContentInput(e.target.value)}
                  rows="8"
                  placeholder="Paste or type your unformatted play text here..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Play Format
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {formatOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFormat(option.value)}
                      className={`flex items-center justify-center p-3 rounded-lg border ${
                        format === option.value
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Style Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Formatting Style
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {styleOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setStyle(option.value)}
                      className={`flex items-center justify-center p-3 rounded-lg border ${
                        style === option.value
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Include Notes Toggle */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setIncludeNotes(!includeNotes)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    includeNotes ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                      includeNotes ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium text-gray-700">
                  Include Stage Directions and Technical Notes
                </span>
              </div>

              <button
                onClick={formatContent}
                disabled={isFormatting}
                className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                  isFormatting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isFormatting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Formatting...
                  </div>
                ) : (
                  'Format Play'
                )}
              </button>
            </div>

            {/* Formatted Content Section */}
            {formattedContent ? (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Formatted Play</h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => copyToClipboard(formattedContent)}
                      className="text-gray-500 hover:text-gray-700 p-2"
                      title="Copy to clipboard"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={downloadAsFile}
                      className="text-gray-500 hover:text-gray-700 p-2"
                      title="Download as file"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="prose max-w-none font-mono">
                  <pre className="whitespace-pre-wrap text-sm">{formattedContent}</pre>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No formatted content yet</h3>
                <p className="mt-1 text-sm text-gray-500">Paste your text and click Format Play to begin.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <DeploymentOptions appId="play-formatter" position="none" />
    </div>
  );
}

export default PlayFormatter; 